import Immutable from 'immutable';
import uniqueId from 'lodash/uniqueId';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { Dropdown, DropdownMenuItem } from '@bibliocommons/base-dropdown';

import {
  closeOverlay as closeOverlayAction,
  openOverlay as openOverlayAction
} from 'app/actions/FullScreenOverlayActions';
import { redirect } from 'app/actions/RedirectActions';
import AddToListOverlay from 'app/components/shared/AddToListOverlay';
import BibBrief from 'app/components/shared/displayBib/BibBrief';
import { FullScreenOverlayContainer } from 'app/components/shared/FullScreenOverlay';
import { convertMetadataIdToLegacyId } from 'app/helpers/bib/bibHelpers';
import { displayBibShape } from 'app/shapes';
import displayAvailabilityShape from 'app/shapes/displayAvailability';

const AddToList = ({ className, displayAvailability, displayBib, trackAddToListClick, trackAddToNewListClick }) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const overlayKey = uniqueId('AddToListOverlay_');

  const { metadataId, title } = displayBib;

  const addToExistingListLabel = intl.formatMessage({ id: 'add_to_list_existing' });
  const addToNewListLabel = intl.formatMessage({ id: 'add_to_list_new' });
  const dropdownLabel = intl.formatMessage({ id: 'add_to_list' });

  const closeOverlay = () => {
    dispatch(closeOverlayAction(overlayKey));
  };

  const handleAddToExistingList = () => {
    dispatch(openOverlayAction(overlayKey, { authRequired: true }));
  };

  const handleAddToNewList = () => {
    const legacyId = convertMetadataIdToLegacyId(metadataId);

    trackAddToNewListClick({
      displayAvailability,
      displayBib,
      linkText: addToNewListLabel
    });

    dispatch(redirect(`/user_lists/new?bib=${legacyId}`, { authRequired: true }));
  };

  const trackAddToListClickProxy = payload => {
    trackAddToListClick({
      displayAvailability,
      displayBib,
      ...payload
    });
  };

  const renderBibBrief = () => {
    return <BibBrief displayBib={displayBib} />;
  };

  return (
    <div className={className}>
      <FullScreenOverlayContainer dataKey="add-to-list-overlay" overlayKey={overlayKey} handleClose={closeOverlay}>
        <AddToListOverlay
          bibTitle={title}
          metadataId={metadataId}
          renderBibBrief={renderBibBrief}
          trackAddToListClick={trackAddToListClickProxy}
        />
      </FullScreenOverlayContainer>
      <Dropdown block dataKey="add-to-list-dropdown" renderLabel={() => dropdownLabel}>
        <DropdownMenuItem
          handleClick={handleAddToNewList}
          item={Immutable.Map({
            label: addToNewListLabel,
            value: 'add-to-new-list'
          })}
        />
        <DropdownMenuItem
          handleClick={handleAddToExistingList}
          item={Immutable.Map({
            label: addToExistingListLabel,
            value: 'add-to-existing-list'
          })}
        />
      </Dropdown>
    </div>
  );
};

AddToList.defaultProps = {
  className: ''
};

AddToList.propTypes = {
  className: PropTypes.string,
  displayAvailability: displayAvailabilityShape.isRequired,
  displayBib: displayBibShape.isRequired,
  trackAddToListClick: PropTypes.func.isRequired,
  trackAddToNewListClick: PropTypes.func.isRequired
};

export default AddToList;
